import React, { useState, useEffect, useRef, useCallback } from 'react'
import { View, StyleSheet, Animated } from 'react-native'
import { Text } from '@libs/components'
import { getBrandIframeConfig } from '../../utils/getOAPUrl'
import { generateSignedData } from '../../utils/generateSignedData'

/**
 * Custom API Iframe Component for brands that use custom API integration
 * This component handles the specific workflow for brands like GIH, Berlin School, etc.
 * Uses direct API calls with HMAC-SHA256 signature generation instead of iframe communication
 */
const CustomApiIframeMobile = ({
  opportunityDetails,
  handleSubmissionConfirmation = () => {},
  mode,
}) => {
  const iframeRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)

  // Animation refs for loader
  const pulseAnim = useRef(new Animated.Value(0.6)).current
  const rotateAnim = useRef(new Animated.Value(0)).current

  // Get brand-specific configuration
  const brandConfig = getBrandIframeConfig(opportunityDetails)

  const scaleAnim = useRef(new Animated.Value(1)).current

  const getApiEndPoint = (mode, config) => {
    const { updateApiEndpoint, createApiEndpoint, changeApiEndPoint } = config
    switch (mode) {
      case 'change':
        return changeApiEndPoint
      case 'update':
        return updateApiEndpoint
      case 'create':
        return createApiEndpoint
      default:
        return updateApiEndpoint
    }
  }

  // Initialize animations
  useEffect(() => {
    if (isLoading) {
      // Pulse animation
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1200,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 0.6,
            duration: 1200,
            useNativeDriver: true,
          }),
        ]),
      ).start()

      // Rotation animation
      Animated.loop(
        Animated.timing(rotateAnim, {
          toValue: 1,
          duration: 3000,
          useNativeDriver: true,
        }),
      ).start()

      // Scale animation
      Animated.loop(
        Animated.sequence([
          Animated.timing(scaleAnim, {
            toValue: 1.1,
            duration: 1500,
            useNativeDriver: true,
          }),
          Animated.timing(scaleAnim, {
            toValue: 1,
            duration: 1500,
            useNativeDriver: true,
          }),
        ]),
      ).start()
    }
  }, [isLoading, pulseAnim, rotateAnim, scaleAnim])

  useEffect(() => {
    if (!brandConfig) {
      setErrorMessage('Brand configuration not found')
      setIsLoading(false)
      return
    }

    // Start loading animations
    const pulseAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 0.6,
          duration: 1000,
          useNativeDriver: true,
        }),
      ]),
    )

    const rotateAnimation = Animated.loop(
      Animated.timing(rotateAnim, {
        toValue: 1,
        duration: 2000,
        useNativeDriver: true,
      }),
    )

    pulseAnimation.start()
    rotateAnimation.start()

    return () => {
      pulseAnimation.stop()
      rotateAnimation.stop()
    }
  }, [brandConfig, pulseAnim, rotateAnim])

  // Cleanup effect to ensure loader is hidden on unmount
  useEffect(
    () => () => {
      setIsLoading(false)
    },
    [],
  )

  useEffect(() => {
    // Set up message listener for submission confirmation

    const handleMessage = (event) => {
      if (
        (event.data && event.data.type === 'OAP_FORM_SUBMITED') ||
        event.data.type === 'CR_FORM_SUBMITED'
      ) {
        console.log('Received submission confirmation in custom api iframe')
        handleSubmissionConfirmation(event.data)
      }
    }

    console.log('Setting up message listener for submission confirmation')

    window.addEventListener('message', handleMessage)

    return () => {
      console.log('Removing message listener for submission confirmation')
      window.removeEventListener('message', handleMessage)
    }
  }, [handleSubmissionConfirmation])

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized || !brandConfig?.config) {
      return
    }

    const initializeCustomApiWithDirectCall = async () => {
      try {
        setIsInitialized(true)
        setIsLoading(true)

        const dateTime = new Date().toISOString().slice(0, 19).replace('T', ' ')

        // Construct URL with signed data parameters
        const { baseUrl } = brandConfig.config

        const apiEndPoint = getApiEndPoint(mode, brandConfig.config)

        const updatePayload = {
          clientId: 'apphero',
          externalId: opportunityDetails.ApplicationFormId__c,
          dateTime,
        }

        const updateSignature = generateSignedData(
          updatePayload,
          opportunityDetails.BusinessUnitFilter__c,
        )

        const iframeUrl = `${baseUrl}${apiEndPoint}?signed_data=${encodeURIComponent(
          updateSignature,
        )}`

        // Wait for iframe to be available
        await new Promise((resolve) => {
          setTimeout(resolve, 500)
        })

        const waitForIframe = () =>
          new Promise((resolve, reject) => {
            let attempts = 0
            const maxAttempts = 10

            const checkIframe = () => {
              const iframe = document.getElementById('custom-api-iframe')
              if (iframe) {
                resolve(iframe)
              } else if (attempts >= maxAttempts) {
                reject(new Error('Custom API iframe not found'))
              } else {
                attempts += 1
                setTimeout(checkIframe, 100)
              }
            }

            checkIframe()
          })

        const iframe = await waitForIframe()

        // Set up message handler for API responses
        const messageHandler = (event) => {
          if (event.data && typeof event.data === 'object') {
            switch (event.data.type) {
              case 'CUSTOM_API_SUBMISSION_CONFIRMATION':
                break
              case 'custom_api_error':
                setErrorMessage(
                  event.data.message || 'Custom API error occurred',
                )
                setIsLoading(false)
                break
              default:
                break
            }
          }
        }

        // Add load event listener before setting src
        const iframeLoadHandler = () => {
          setIsLoading(false)
          iframe.removeEventListener('load', iframeLoadHandler)
        }

        iframe.addEventListener('load', iframeLoadHandler)
        window.addEventListener('message', messageHandler)

        // Update iframe src with signed data
        iframe.src = iframeUrl

        // Safety timeout to hide loader
        const safetyTimeout = setTimeout(() => {
          setIsLoading(false)
        }, 10000) // 10 seconds timeout

        // Cleanup function
        return () => {
          window.removeEventListener('message', messageHandler)
          iframe.removeEventListener('load', iframeLoadHandler)
          clearTimeout(safetyTimeout)
        }
      } catch (error) {
        setErrorMessage(
          `Failed to initialize Custom API Portal: ${error.message}`,
        )
        setIsLoading(false)
        setIsInitialized(false) // Reset on error to allow retry
      }
    }

    initializeCustomApiWithDirectCall()
  }, [brandConfig, opportunityDetails?.ApplicationFormId__c, isInitialized])

  if (!brandConfig) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>
          Brand configuration not available for{' '}
          {opportunityDetails?.BusinessUnitFilter__c}
        </Text>
      </View>
    )
  }

  if (errorMessage) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{errorMessage}</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <View style={styles.iframeContainer}>
        <iframe
          id="custom-api-iframe"
          ref={iframeRef}
          src="about:blank"
          style={styles.iframe}
          title={`${opportunityDetails?.BusinessUnitFilter__c} Portal`}
        />
      </View>
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingContent}>
            {/* Creative animated loader */}
            <View style={styles.loaderContainer}>
              {/* Outer rotating ring */}
              <Animated.View
                style={[
                  styles.outerRing,
                  {
                    transform: [
                      {
                        rotate: rotateAnim.interpolate({
                          inputRange: [0, 1],
                          outputRange: ['0deg', '360deg'],
                        }),
                      },
                    ],
                  },
                ]}
              />

              {/* Inner pulsing circle */}
              <Animated.View
                style={[
                  styles.innerCircle,
                  {
                    opacity: pulseAnim,
                    transform: [{ scale: scaleAnim }],
                  },
                ]}
              >
                <View style={styles.portalIcon}>
                  <Text style={styles.portalIconText}>🌐</Text>
                </View>
              </Animated.View>

              {/* Floating dots */}
              {[0, 1, 2].map((index) => (
                <Animated.View
                  key={index}
                  style={[
                    styles.floatingDot,
                    {
                      opacity: pulseAnim,
                      transform: [
                        {
                          rotate: rotateAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [
                              `${index * 120}deg`,
                              `${index * 120 + 360}deg`,
                            ],
                          }),
                        },
                      ],
                    },
                  ]}
                />
              ))}
            </View>

            {/* Loading text */}
            <Animated.View
              style={[styles.textContainer, { opacity: pulseAnim }]}
            >
              <Text style={styles.loadingText}>Connecting to Portal</Text>
              <Text style={styles.loadingSubText}>
                Setting up secure connection...
              </Text>
            </Animated.View>
          </View>
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
    minHeight: '700px',
  },
  iframeContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: 12,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    display: 'flex',
  },
  loadingContent: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    width: '100%',
  },
  loaderContainer: {
    position: 'relative',
    width: 120,
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  outerRing: {
    position: 'absolute',
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 3,
    borderColor: '#3b82f6',
    borderStyle: 'dashed',
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
  },
  innerCircle: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: '#eff6ff',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#3b82f6',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  portalIcon: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  portalIconText: {
    fontSize: 28,
  },
  floatingDot: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#3b82f6',
    top: 10,
    left: '50%',
    marginLeft: -4,
    transformOrigin: '4px 46px',
  },
  textContainer: {
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    color: '#FF3B30',
    textAlign: 'center',
  },
})

export default CustomApiIframeMobile
